import type { AxiosRequestConfig } from "axios"
import JSEncrypt from "jsencrypt"
import { stringify } from "qs"
import cryptoUtil from "./crypto"

const ENCRYPTION_TYPE = {
    PARAMS_ONLY: 1,
    FORM_DATA: 2,
    JSON_BODY: 3,
} as const

type EncryptionType = (typeof ENCRYPTION_TYPE)[keyof typeof ENCRYPTION_TYPE]

interface EncryptionPayload {
    nonce: string
    sid: string
    timestamp: number
    msg_params?: string | Record<string, any>
    params?: string
    signature?: string
}

// 请求时参数不加密的url
const whiteList: string[] = [
    "/captchaImage",
    "/apidocs/**",
    "/common/**",
    "/payment/**",
    "/api/**",
    "/system/appVersion/versionVerify",
    "/system/multilingualVersion/latest",
    "/system/subscription/**",
    "/system/tenantOrder/handleTrial",
    "/system/crmApp/**",
    "/system/crmAppOrder",
    "/system/crmAppOrder/**",
    "/system/lead/add",
    "/system/tenant/initTenantId",
    "/profile/**",
]

// header中不添加sid(来自userInfo里的userId)的url
// const sidWhiteList = ["/common/**", "/user/login/**", "/user/register/**", "/system/mainfest"]

//  检查 URL 是否匹配配置列表中的模式
// Cache compiled regex patterns
function shouldSkipEncryption(url: string): boolean {
    return whiteList.some((pattern) => {
        if (pattern.includes("**")) {
            // 取 ** 前缀部分
            const prefix = pattern.split("**")[0]
            return url.startsWith(prefix)
        }
        return url === pattern
    })
}

// 检查 URL 是否应该跳过添加sid
// function shouldSkipSid(url: string): boolean {
//   return sidWhiteList.some((pattern) => {
//     if (pattern.includes("**")) {
//       // 取 ** 前缀部分
//       const prefix = pattern.split("**")[0]
//       return url.startsWith(prefix)
//     } else {
//       return url === pattern
//     }
//   })
// }

export function encryptTranPwd(
    passwd: string,
    useDefaultKey: boolean = false
): string {
    const jse = new JSEncrypt()
    const publicKey =
        "MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQDDgTt/niY5uJrxHZ0JkPSDgYljqPgBtFkUoyUel5kEt2gxIAjcFy1Qehu+aLUUkdbWqaJDpugabJeLldyTc7zz5jD8i24tEOi+2XH5XvP6uBnIUQA65cGMkiAjEWHz/4UPCzgaDN7iAweMljAFfSOlXw3Vgd/py4MP30oXNxEQXQIDAQAB"
    // 设置公钥：如果useDefaultKey为true，使用默认密钥；否则优先使用localStorage中的密钥
    jse.setPublicKey(
        useDefaultKey
            ? publicKey
            : localStorage.getItem("publicKey") || publicKey
    )
    return jse.encrypt(passwd) || ""
}

function getQuery(url: string): Record<string, string> {
    const q: Record<string, string> = {}
    if (url && typeof url === "string") {
        const query = url.split("?")[1]
        if (!query) return q
        const vars = query.split("&")
        for (let i = 0; i < vars.length; i += 1) {
            const pair = vars[i].split("=")
            q[pair[0]] = decodeURIComponent(pair[1])
        }
    }
    return q
}

function encryptMsg(
    config: AxiosRequestConfig,
    sid: string,
    type: EncryptionType
): void {
    try {
        if (import.meta.env.VITE_APP_DEBUG === "true") {
            /* eslint-disable no-console */
            console.groupCollapsed(
                `${config.method}: ${config.url?.split("?")?.[0]}`
            ) // Collapsible group header
            console.log("Params/Data:", config.params || config.data) // Separate line for parameters/data
            console.log("config info:", config) // Separate line for URL
            console.groupEnd() // End of group
            /* eslint-enable no-console */
        }

        let params: string | Record<string, any> = ""
        if (type === ENCRYPTION_TYPE.PARAMS_ONLY) {
            if (config.params?.params) {
                params = stringify(config.params.params)
            } else if (config.params) {
                params = stringify(config.params)
            } else {
                params = ""
            }
        } else if (type === ENCRYPTION_TYPE.FORM_DATA) {
            params = stringify(JSON.parse(JSON.stringify(config.data)), {
                arrayFormat: "repeat",
            })
        } else {
            params = JSON.parse(JSON.stringify(config.data))
        }
        const timestamp = new Date().getTime()
        const nonce = cryptoUtil.randomResult(16)
        const fundKey = cryptoUtil.randomResult(32)
        const payload: EncryptionPayload = {
            nonce,
            sid,
            timestamp,
        }
        if (
            type === ENCRYPTION_TYPE.PARAMS_ONLY ||
            type === ENCRYPTION_TYPE.FORM_DATA
        ) {
            payload.msg_params = params
        } else {
            payload.params = ""
        }
        const signature = cryptoUtil.sha512Encrypt(
            cryptoUtil.sortParams(payload)
        )
        const msg_encrypt = cryptoUtil.aesEncrypt(
            stringify({
                ...payload,
                signature,
            }),
            fundKey
        )
        const body_encrypt = params
            ? cryptoUtil.aesEncrypt(JSON.stringify(params), fundKey)
            : ""
        if (
            type === ENCRYPTION_TYPE.PARAMS_ONLY ||
            type === ENCRYPTION_TYPE.JSON_BODY
        ) {
            config.url += `?msg_encrypt=${msg_encrypt}`
        }

        if (type === ENCRYPTION_TYPE.PARAMS_ONLY) {
            config.data = null
        } else if (type === ENCRYPTION_TYPE.FORM_DATA) {
            config.data = { msg_encrypt }
        } else {
            config.data = { body_encrypt }
        }
        config.params = null

        if (!config.headers) {
            config.headers = {}
        }
        config.headers.fundKey = encryptTranPwd(fundKey)
        config.headers.sid = sid
        // // 检查是否在sidWhiteList中
        // const skipSid = shouldSkipSid(config.url)
        // // 对于sidWhiteList中的接口使用默认密钥，否则使用localStorage中的密钥
        // config.headers["fundKey"] = encryptTranPwd(fundKey, skipSid)
        // // 检查是否需要添加sid
        // if (!skipSid) {
        //   config.headers["sid"] = sid
        // }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error("加密失败", e)
    }
}

export function assembleConfig(config: AxiosRequestConfig): void {
    if (!config.url) return

    if (!shouldSkipEncryption(config.url)) {
        const sid: string = localStorage.getItem("uuid") || ""
        // 没有params和data但是通过?传参数的url 处理成params
        if (config.url.indexOf("?") !== -1) {
            const p = getQuery(config.url)
            if (config.params) {
                Object.assign(config.params, p)
            } else {
                config.params = p
            }
            config.url = config.url.split("?")[0]
        }
        // 通过?传参数的url 或者者没有params和data的url
        if (config.params || (!config.params && !config.data)) {
            encryptMsg(config, sid, ENCRYPTION_TYPE.PARAMS_ONLY)
            return
        }
        if (
            config.data &&
            config.headers?.["Content-Type"] ===
                "application/x-www-form-urlencoded"
        ) {
            encryptMsg(config, sid, ENCRYPTION_TYPE.FORM_DATA)
            return
        }
        if (
            config.data &&
            config.headers?.["Content-Type"] !==
                "application/x-www-form-urlencoded"
        ) {
            encryptMsg(config, sid, ENCRYPTION_TYPE.JSON_BODY)
        }
    }
}
